/* eslint-disable max-len */
/* eslint-disable react/display-name */
import React, { useCallback, useMemo, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { LFC } from '../../../Legacy';
import { useResolve } from '../../Container';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Popup, PopupProps } from '../../popups/Popup';
import { Prompt } from './../../../ui';
import { ExportConfigurationView } from './ExportConfigurationView';
import { ExportOutputView } from './ExportOutputView';
import { ExportConfiguration, ExportService, SerializedExportConfiguration } from './ExportService';
import { ExportSource, ExportSourceLibrary } from './ExportSource';
import { ExportTypes } from './ExportTypes';

const selectInitialSource = (sourceLib: ExportSourceLibrary) => {
    return sourceLib.getAll().flatMap(x => x.get()).find(x => x.default && x.canRead());
};

const selectDefaultTypeForSource = (source: ExportSource, lastConfig?: SerializedExportConfiguration) => {
    const items = source.read().filter(x => !!ExportTypes.find(t => t.key === x.type));
    const unique = new Set(items.map(x => x.type));
    const counted = Array.from(unique).map(type => ({ type, count: items.filter(x => x.type === type).length }));
    const sorted = counted.sort((a, b) => b.count - a.count).map(x => x.type);
    if (sorted.includes(lastConfig?.type)) return lastConfig.type;
    // Specifically move token to last
    const swizzled = [...sorted.filter(x => x !== 'Token'), ...sorted.filter(x => x === 'Token')];
    return swizzled[0] ?? null;
};

export const exportPopup = () => ({ ui: ExportPopup, args: {} });

export const ExportPopup: LFC<PopupProps> = ({ action, dismiss, args }) => {

    const board = useResolve(EditorSession).get(BoardInfo);
    const exporter = useResolve(ExportService);
    const [lastConfig] = useSessionStorage<SerializedExportConfiguration>(`deqo/board/${board.id}/exportConfig`);

    const sourceLib = useResolve(ExportSourceLibrary);
    const defaultSource = useMemo(() => selectInitialSource(sourceLib), []);
    const defaultType = useMemo(() => selectDefaultTypeForSource(defaultSource, lastConfig), []);

    const baseConfig = useMemo(() => !!lastConfig ? exporter.deserializeConfig(lastConfig) : {}, []);
    const initialConfig = useMemo(() => ({ ...baseConfig, source: defaultSource, type: defaultType }), []);

    const [max, setMax] = useState(false);
    const [config, setConfig] = useState(initialConfig as ExportConfiguration);
    const [step, setStep] = useState(0);

    const onConfigured = useCallback((value: ExportConfiguration) => {
        setConfig(value);
        setStep(1);
    }, []);

    const onBack = useCallback(() => {
        setStep(0);
    }, [action, config]);

    const onComplete = useCallback((save: boolean) => {
        action({ config, save });
    }, [action, config]);

    return <Prompt ignoreEnter onAction={dismiss}>
        <Popup w={80} h={48} maximize={max}>
            <Popup.Close onAction={dismiss} />
            <Popup.MinMax value={max} onAction={setMax} />
            <Popup.Title id="export-data">Export Data</Popup.Title>
            <Popup.Content>
                {step === 0 && <ExportConfigurationView config={config} onNext={onConfigured} />}
                {step === 1 && <ExportOutputView config={config} onBack={onBack} onComplete={onComplete} />}
            </Popup.Content>
        </Popup>
    </Prompt>;
};


